import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { DealCard, Sale } from "../types";
import { elapsed } from "./cardStatus";
import { fullName, useRefreshFunnel, useStage } from "./hooks";
import { StageBadge } from "./shared";

/** "Втрачені" segment of the funnel (docs/design/FunnelLost.dc.html) */
export const LostList = ({ filter }: { filter: Record<string, unknown> }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider();
  const stage = useStage();
  const { dealLostReasons } = useConfigurationContext();
  const [reason, setReason] = useState("");
  const refresh = useRefreshFunnel();
  const { data: lost = [] } = useGetList<DealCard>("deal_cards", {
    filter: { ...filter, stage: "lost" },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "lost_at", order: "DESC" },
  });
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  const countOf = (value: string) =>
    lost.filter((deal) => deal.lost_reason === value).length;
  const reasonLabel = (value: string | null | undefined) =>
    dealLostReasons.find((r) => r.value === value)?.label ?? value ?? "";
  const managerName = (id: unknown) => {
    const sale = sales.find((s) => s.id === id);
    return sale ? `${sale.first_name} ${sale.last_name.charAt(0)}.` : "";
  };
  const ago = (date: string | null | undefined) => {
    if (!date) return "";
    const { count, unit } = elapsed(new Date(date), new Date());
    return `${count} ${translate(`crm.funnel.units.${unit}`)}`;
  };
  const shown = reason ? lost.filter((d) => d.lost_reason === reason) : lost;
  const top = [...dealLostReasons]
    .map((r) => ({ ...r, count: countOf(r.value) }))
    .sort((a, b) => b.count - a.count)[0];

  const restore = async (deal: DealCard) => {
    try {
      await dataProvider.update("deals", {
        id: deal.id,
        data: { stage: "new" },
        previousData: deal,
      });
      notify("crm.funnel.lost_list.restored");
      refresh();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  const chips = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {translate("crm.funnel.lost_list.reason")}
      </span>
      {[
        { value: "", label: translate("crm.funnel.lost_list.all") },
        ...dealLostReasons,
      ]
        .filter((r) => r.value === "" || countOf(r.value) > 0)
        .map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => setReason(r.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-sm font-semibold",
              reason === r.value
                ? "border-primary bg-primary text-primary-foreground"
                : "bg-card",
            )}
          >
            {r.label} · {r.value ? countOf(r.value) : lost.length}
          </button>
        ))}
    </div>
  );

  if (lost.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("crm.funnel.lost_list.empty")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {chips}
      {isMobile ? (
        <div className="flex flex-col gap-2">
          {shown.map((deal) => (
            <div key={deal.id} className="rounded-xl border bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <Link to={`/funnel/${deal.id}`} className="font-bold">
                  {fullName(deal)}
                </Link>
                <StageBadge stage={stage(deal.lost_from_stage)} />
              </div>
              <div className="text-sm text-muted-foreground">
                {reasonLabel(deal.lost_reason)} · {ago(deal.lost_at)}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => restore(deal)}
              >
                {translate("crm.funnel.lost_list.restore")}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">
                  {translate("crm.funnel.lost_list.client")}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {translate("crm.funnel.lost_list.reason")}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {translate("crm.funnel.lost_list.lost_at_stage")}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {translate("crm.funnel.lost_list.manager")}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {translate("crm.funnel.lost_list.when")}
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((deal) => (
                <tr key={deal.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-bold">
                    <Link to={`/funnel/${deal.id}`}>{fullName(deal)}</Link>
                  </td>
                  <td className="px-4 py-3">
                    {reasonLabel(deal.lost_reason)}
                    {deal.lost_comment ? (
                      <div className="text-xs text-muted-foreground">
                        {deal.lost_comment}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <StageBadge stage={stage(deal.lost_from_stage)} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {managerName(deal.sales_id)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {ago(deal.lost_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => restore(deal)}
                    >
                      {translate("crm.funnel.lost_list.restore")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {top && top.count > 0
          ? `${translate("crm.funnel.lost_list.top_reason", {
              reason: top.label,
              count: top.count,
              total: lost.length,
            })} `
          : ""}
        {translate("crm.funnel.lost_list.hint")}
      </p>
    </div>
  );
};
